import { useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiDelete, mdiKeyChange, mdiPencil, mdiSend, mdiTimelineText } from '@mdi/js'
import { $api } from '../../api/queryClient'
import type { WebhookSubscription } from '../../api/schema'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorCode } from '../../lib/apiError'
import { notify } from '../../lib/notifications'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { SecretReveal } from '../shared/SecretReveal'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { EmptyState } from '../ui/empty-state'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Skeleton } from '../ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'

const LIST = '/api/v1/organizations/{orgCode}/webhooks/subscriptions'

/** Refreshes every webhooks query of the organization. */
export function useRefreshWebhooks(org: string) {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] !== 'get' || typeof key[1] !== 'string' || !key[1].startsWith('/api/v1/organizations/{orgCode}/webhooks')) return false
                const params = key.find((k) => k && typeof k === 'object' && 'params' in (k as object)) as { params?: { path?: { orgCode?: string } } } | undefined
                return params?.params?.path?.orgCode === org
            },
        })
}

type FieldErrors = { url?: string; event_types?: string; form?: string }

/** The create and edit dialog. A server error shows on its field. */
function WebhookFormDialog({ org, open, onOpenChange, editing, onCreated }: {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    editing: WebhookSubscription | null
    onCreated: (secret: string) => void
}) {
    const { t } = useTranslation('webhooks')
    const refresh = useRefreshWebhooks(org)
    const [url, setUrl] = useState('')
    const [description, setDescription] = useState('')
    const [types, setTypes] = useState<string[]>([])
    const [errors, setErrors] = useState<FieldErrors>({})
    const { data: catalogue } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/webhooks/event-types', { params: { path: { orgCode: org } } }, { enabled: open })

    useEffect(() => {
        if (!open) return
        setUrl(editing?.url ?? '')
        setDescription(editing?.description ?? '')
        setTypes(editing?.event_types ?? [])
        setErrors({})
    }, [open, editing])

    const fail = (err: unknown) => {
        const code = apiErrorCode(err) ?? 'generic'
        const message = t(`errors.${code}`, { defaultValue: t('errors.generic') })
        if (code === 'webhook_url_invalid' || code === 'webhook_url_not_public') setErrors({ url: message })
        else if (code === 'webhook_event_type_unknown') setErrors({ event_types: message })
        else setErrors({ form: message })
    }
    const create = $api.useMutation('post', LIST, {
        onSuccess: (res) => {
            refresh()
            onOpenChange(false)
            onCreated(res.subscription.secret)
        },
        onError: fail,
    })
    const update = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}', {
        onSuccess: () => {
            refresh()
            onOpenChange(false)
        },
        onError: fail,
    })
    const save = () => {
        const body = { url, description, event_types: types }
        if (editing) update.mutate({ params: { path: { orgCode: org, subscriptionUid: editing.uid } }, body })
        else create.mutate({ params: { path: { orgCode: org } }, body })
    }
    const toggle = (type: string, on: boolean) => setTypes((prev) => (on ? [...prev, type] : prev.filter((x) => x !== type)))

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="webhook-form">
                <DialogHeader>
                    <DialogTitle>{t(editing ? 'form.edit_title' : 'form.create_title')}</DialogTitle>
                    <DialogDescription>{t('description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="mb-1 block text-sm" htmlFor="webhook-url">{t('form.url')}</Label>
                        <Input id="webhook-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" data-testid="webhook-url-input" aria-invalid={!!errors.url} />
                        {errors.url && <p className="mt-1 text-sm text-destructive" data-testid="webhook-url-error">{errors.url}</p>}
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm" htmlFor="webhook-description">{t('form.description')}</Label>
                        <Input id="webhook-description" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="webhook-description-input" />
                    </div>
                    <fieldset>
                        <legend className="mb-1 text-sm font-medium">{t('form.events')}</legend>
                        <div className="space-y-1">
                            {(catalogue?.event_types ?? []).map((e) => (
                                <label key={e.type} className="flex items-center gap-2 text-sm">
                                    <Checkbox checked={types.includes(e.type)} onCheckedChange={(on) => toggle(e.type, on)} data-testid={`webhook-event-checkbox-${e.type}`} />
                                    <span>{t(`event_types.${e.type}`, { defaultValue: e.type })}</span>
                                    <code className="text-xs text-muted-foreground">{e.type} v{e.schema_version}</code>
                                </label>
                            ))}
                        </div>
                        {errors.event_types && <p className="mt-1 text-sm text-destructive" data-testid="webhook-event-types-error">{errors.event_types}</p>}
                    </fieldset>
                    {errors.form && <p className="text-sm text-destructive" data-testid="webhook-form-error">{errors.form}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="webhook-cancel-button">{t('form.cancel')}</Button>
                    <Button onClick={save} disabled={create.isPending || update.isPending} data-testid="webhook-save-button">{t('form.save')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** A confirmation with its own test ids (rotate, delete). */
function Confirm({ open, onOpenChange, title, description, confirm, testId, onConfirm, destructive }: {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    description: string
    confirm: string
    testId: string
    onConfirm: () => void
    destructive?: boolean
}) {
    const { t } = useTranslation('webhooks')
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent data-testid={`${testId}-dialog`}>
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`${testId}-cancel`}>{t('form.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                        data-testid={testId}
                        className={destructive ? 'bg-destructive text-white hover:bg-destructive/90' : undefined}
                        onClick={(e) => {
                            e.preventDefault()
                            onConfirm()
                        }}
                    >
                        {confirm}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

/** The organization's outgoing webhooks (spec 2026-10-25-13 step 8). */
export function WebhooksSettingsPage() {
    const { org } = useParams({ from: '/$org/settings/webhooks/' })
    const { t } = useTranslation('webhooks')
    const { formatDate } = useFormatters()
    const canWrite = useCan('webhooks.write')
    const refresh = useRefreshWebhooks(org)
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('settings:page.title'), path: `/${org}/settings` },
        { title: t('title') },
    ])
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<WebhookSubscription | null>(null)
    // The secret lives in memory only, until the reveal closes.
    const [secret, setSecret] = useState<string | null>(null)
    const [rotating, setRotating] = useState<WebhookSubscription | null>(null)
    const [deleting, setDeleting] = useState<WebhookSubscription | null>(null)

    const { data, isLoading } = $api.useQuery('get', LIST, { params: { path: { orgCode: org } } })
    const rows = data?.subscriptions ?? []
    const path = (uid: string) => ({ params: { path: { orgCode: org, subscriptionUid: uid } } })
    const failed = (err: unknown) =>
        notify({ key: 'webhooks_error', variant: 'destructive', titleKey: `webhooks:errors.${apiErrorCode(err) ?? 'generic'}` })
    const toggle = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}', { onSuccess: () => refresh(), onError: failed })
    const rotate = $api.useMutation('post', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}/rotate-secret', {
        onSuccess: (res) => {
            setRotating(null)
            setSecret(res.subscription.secret)
            refresh()
        },
        onError: failed,
    })
    const test = $api.useMutation('post', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}/test', {
        onSuccess: () => {
            refresh()
            notify({ key: 'webhooks_test', variant: 'default', titleKey: 'webhooks:actions.test_sent' })
        },
        onError: failed,
    })
    const remove = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}', {
        onSuccess: () => {
            setDeleting(null)
            refresh()
        },
        onError: failed,
    })

    return (
        <div className="space-y-6" data-testid="webhooks-page">
            <PageHeader
                icon={SECTION_ICONS.webhooks}
                title={t('title')}
                description={t('description')}
                actions={canWrite ? (
                    <Button onClick={() => { setEditing(null); setFormOpen(true) }} data-testid="webhook-create-button">{t('create_button')}</Button>
                ) : undefined}
            />

            {isLoading ? (
                <Skeleton className="h-32 w-full" />
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={SECTION_ICONS.webhooks}
                    title={t('empty.title')}
                    description={t('empty.description')}
                    data-testid="webhooks-empty-state"
                />
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('columns.url')}</TableHead>
                                    <TableHead>{t('columns.events')}</TableHead>
                                    <TableHead>{t('columns.enabled')}</TableHead>
                                    <TableHead className="text-right">{t('columns.pending')}</TableHead>
                                    <TableHead className="text-right">{t('columns.failed')}</TableHead>
                                    <TableHead>{t('columns.last_delivery')}</TableHead>
                                    <TableHead className="text-right">{t('columns.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((s) => (
                                    <TableRow key={s.uid} data-testid="webhook-subscription-row" data-uid={s.uid}>
                                        <TableCell className="max-w-xs">
                                            <div className="truncate font-mono text-sm" title={s.url}>{s.url}</div>
                                            {s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {s.event_types.map((e) => (
                                                    <Badge key={e} variant="outline">{t(`event_types.${e}`, { defaultValue: e })}</Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={s.enabled}
                                                disabled={!canWrite || toggle.isPending}
                                                aria-label={t('columns.enabled')}
                                                onCheckedChange={(on) => toggle.mutate({ ...path(s.uid), body: { enabled: on } })}
                                                data-testid="webhook-enabled-switch"
                                            />
                                        </TableCell>
                                        <TableCell className="text-right" data-testid="webhook-pending-count">{s.pending_count}</TableCell>
                                        <TableCell className="text-right" data-testid="webhook-failed-count">
                                            {s.failed_count > 0 ? <Badge variant="destructive">{s.failed_count}</Badge> : 0}
                                        </TableCell>
                                        <TableCell className="text-sm">{s.last_delivery_at ? formatDate(s.last_delivery_at, { dateStyle: 'short', timeStyle: 'short' }) : t('never')}</TableCell>
                                        <TableCell>
                                            <div className="flex justify-end gap-1">
                                                <Button asChild variant="outline" size="icon" aria-label={t('actions.log')} title={t('actions.log')} data-testid="webhook-log-link">
                                                    <Link to="/$org/settings/webhooks/$subscriptionUid" params={{ org, subscriptionUid: s.uid }} search={{ status: undefined }}>
                                                        <AppIcon path={mdiTimelineText} size="sm" />
                                                    </Link>
                                                </Button>
                                                {canWrite && (
                                                    <>
                                                        <Button variant="outline" size="icon" aria-label={t('actions.edit')} title={t('actions.edit')}
                                                            onClick={() => { setEditing(s); setFormOpen(true) }} data-testid="webhook-edit-button">
                                                            <AppIcon path={mdiPencil} size="sm" />
                                                        </Button>
                                                        <Button variant="outline" size="icon" aria-label={t('actions.test')} title={t('actions.test')}
                                                            disabled={test.isPending} onClick={() => test.mutate(path(s.uid))} data-testid="webhook-send-test-button">
                                                            <AppIcon path={mdiSend} size="sm" />
                                                        </Button>
                                                        <Button variant="outline" size="icon" aria-label={t('actions.rotate')} title={t('actions.rotate')}
                                                            onClick={() => setRotating(s)} data-testid="webhook-rotate-secret-button">
                                                            <AppIcon path={mdiKeyChange} size="sm" />
                                                        </Button>
                                                        <Button variant="destructive" size="icon" aria-label={t('actions.delete')} title={t('actions.delete')}
                                                            onClick={() => setDeleting(s)} data-testid="webhook-delete-button">
                                                            <AppIcon path={mdiDelete} size="sm" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <WebhookFormDialog org={org} open={formOpen} onOpenChange={setFormOpen} editing={editing} onCreated={setSecret} />

            <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
                <DialogContent data-testid="webhook-secret-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('secret.title')}</DialogTitle>
                    </DialogHeader>
                    {secret && <SecretReveal secret={secret} onceLabel={t('secret.once')} copyLabel={t('secret.copy')} copiedLabel={t('secret.copied')} />}
                    <DialogFooter>
                        <Button onClick={() => setSecret(null)} data-testid="webhook-secret-close">{t('secret.close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Confirm
                open={!!rotating}
                onOpenChange={(o) => !o && setRotating(null)}
                title={t('rotate.title')}
                description={t('rotate.description')}
                confirm={t('rotate.confirm')}
                testId="webhook-rotate-confirm"
                onConfirm={() => rotating && rotate.mutate(path(rotating.uid))}
            />
            <Confirm
                open={!!deleting}
                onOpenChange={(o) => !o && setDeleting(null)}
                title={t('delete.title')}
                description={t('delete.description')}
                confirm={t('delete.confirm')}
                testId="webhook-delete-confirm"
                destructive
                onConfirm={() => deleting && remove.mutate(path(deleting.uid))}
            />
        </div>
    )
}
